import { RichText } from '@/components/RichText';
import { useTermsSettings } from '@/hooks/useTermsSettings';
import { cn } from '@/lib/utils';
import { termsSettingsEmpty } from '@/types/types_settings';

/**
 * The NDA and terms of service a partner manager accepts at step 2.
 *
 * The wording is not here: it is `settings/terms`, edited on the admin Settings
 * screen, because a legal agreement is the business's to revise and should not
 * need a deploy to correct. What stays here is how it is *presented* — the two
 * blocks, the rule between them, and the colours that make prose readable on the
 * dark registration panel.
 *
 * There is no wording compiled in as a fallback, deliberately: one agreement,
 * one place, so "which version did they accept?" cannot have two answers. Until
 * it is written, this says so.
 *
 * Read live rather than passed in, so the two callers (the registration wizard
 * and `PartnerOnboardingGate`) show the same thing without either having to know
 * it is stored. English only for now; when the Japanese translation lands, the
 * stored document grows a second pair of fields and this grows a toggle.
 *
 * Substantive edits to the wording should come with a bump to
 * PARTNER_TERMS_VERSION, so accepted-before and accepted-after stay
 * distinguishable on the partner record. Nothing enforces that yet.
 */
export function PartnerTermsText() {
  const { settings, loading } = useTermsSettings();

  if (loading) return <p className='tw:text-white/50'>Loading the agreement…</p>;

  // Nothing written, or the read failed. Said plainly rather than left as an
  // empty box above an "I agree" checkbox — a manager should never be asked to
  // agree to a blank.
  if (termsSettingsEmpty(settings)) {
    return (
      <p className='tw:text-white/50'>
        The partner agreement is not available at the moment. Please contact Eighty Days before
        continuing.
      </p>
    );
  }

  return (
    <>
      {settings.ndaBody !== '' && (
        <div className='ed-nda-section tw:mb-8 tw:pl-4'>
          {settings.ndaHeading !== '' && (
            <h3 className='tw:mb-4 tw:font-serif tw:text-xl tw:text-ed-gold'>{settings.ndaHeading}</h3>
          )}
          <RichText html={settings.ndaBody} className='tw:text-white/70 tw:[&_strong]:text-white' />
        </div>
      )}

      {/* The rule separates the two blocks, so it is only drawn when there are two. */}
      {settings.serviceBody !== '' && (
        <div className={cn(settings.ndaBody !== '' && 'tw:border-t tw:border-white/10 tw:pt-6')}>
          {settings.serviceHeading !== '' && (
            <h3 className='tw:mb-4 tw:font-serif tw:text-lg tw:text-ed-gold'>{settings.serviceHeading}</h3>
          )}
          <RichText html={settings.serviceBody} className='tw:text-white/70 tw:[&_strong]:text-white' />
        </div>
      )}
    </>
  );
}
